import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsAlt,
  faBan,
  faLock,
  faUnlock,
} from '@fortawesome/free-solid-svg-icons';
import { AssetType, Layer } from '../../../types/typing.ts';
import './layerpanel.css';
import TextIconSrc from '../../../assets/icons/navigationmenu/TextIcon.svg';

interface LayerPanelProps {
  layers: Layer[];
  handleLayerLock: (index: number) => void;
  onLayerDrag: (draggedIndex: number, droppedIndex: number) => void;
}

const ItemType = 'LAYER';

const DraggableLayer: React.FC<{
  layer: Layer;
  index: number;
  onLayerDrag: (draggedIndex: number, droppedIndex: number) => void;
  handleLayerLock: (index: number) => void;
  isDraggable: boolean;
}> = ({ layer, index, onLayerDrag, handleLayerLock, isDraggable }) => {
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  };
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemType,
    item: { index },
    canDrag: isDraggable,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: ItemType,
    hover: (draggedItem: { index: number }) => {
      if (draggedItem.index !== index) {
        onLayerDrag(draggedItem.index, index);
        draggedItem.index = index; // Update dragged item's index
      }
    },
  });

  return (
    <div
      ref={(node) => dragRef(dropRef(node))}
      className="layer-item"
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isDraggable ? 'move' : 'not-allowed',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px',
        borderBottom: '1px solid #ccc',
      }}
    >
      {layer.imageUrl ? (
        <img
          src={layer.imageUrl}
          alt={layer.name}
          style={{ width: '50px', height: '30px', marginRight: '8px' }}
        />
      ) : (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {layer.type === AssetType.TEXT && (
            <img src={TextIconSrc} alt="Text Icon" className="text-icon" />
          )}
          {truncateText(layer.name, 16)}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FontAwesomeIcon icon={isDraggable ? faArrowsAlt : faBan} />
        <button onClick={() => handleLayerLock(index)}>
          <FontAwesomeIcon icon={layer.locked ? faLock : faUnlock} />
        </button>
      </div>
    </div>
  );
};

const LayerPanel: React.FC<LayerPanelProps> = ({
  layers,
  handleLayerLock,
  onLayerDrag,
}) => {
  return (
    <div className="layer-panel">
      {layers.map((layer, index) => (
        <DraggableLayer
          key={layer.id}
          layer={layer}
          index={index}
          onLayerDrag={onLayerDrag}
          handleLayerLock={handleLayerLock}
          isDraggable={layer.type !== AssetType.BACKGROUND}
        />
      ))}
    </div>
  );
};

export default LayerPanel;
